import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { columns } from '../../../components/columns/Company/FilesColumns';
import { onDownloadItem } from '../../../utils/functions/file.functions';
import { FileUpload } from '../../../components/files/FileUpload';
import { Loader } from '../../../components/loader/Loader';
import { AttachmentType, EntityType, FileBase } from '../../../types/Files';
import { useParams } from 'react-router-dom';
import { useLoading } from '../../../context/loadingContext';
import { supportedFileType } from '../../../utils/functions/data.functions';
import { Modal } from '../../../components/modal/modal';
import FilePreview from '../../../components/filePreview/FilePreview';
import { useTranslation } from '../../../context/translationContext';

/**
 * Page component represents list of files for user task and upload interface
 */
export const UserTaskFiles = () => {
  const { t } = useTranslation();
  const { company } = useCurrentCompany();
  const { id } = useParams();
  const [files, setFiles] = useState<FileBase[] | null>(null);
  const { startLoading, stopLoading } = useLoading();
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>();
  /**
   * Loads files for current task
   */
  const fetchFiles = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.FILE_LIST}?companyId=${company.id}&entityType=${EntityType.USER_TASK}&entityId=${id}`,
    });
    setFiles(response?.data);
  };

  useEffect(() => {
    if (!company || !company.id) return;
    fetchFiles();
  }, [company]);

  /**
   * Removes file
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.FILE_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchFiles();
    }
  };

  /**
   * Opens preview of file
   */
  const handleOpenPreview = (id: string) => {
    setSelectedId(id);
    setPreviewOpen(true);
  };

  if (!company) return <Loader />;
  return (
    <>
      <div className="text-right">
        <FileUpload
          apiPostFilesUrl={`${apiUrl.FILE_UPLOAD_AND_ASSIGN}?companyId=${company.id}&entityType=${EntityType.USER_TASK}&attachmentType=${AttachmentType.OTHER}&entityId=${id}`}
          onUpload={() => fetchFiles()}
        />
      </div>
      <DataTable
        data={files}
        columns={columns}
        handleRemove={onRemoveItem}
        customButtons={[
          {
            title: t('general.preview'),
            color: 'blue',
            onSubmit: (e) => handleOpenPreview(e.toString()),
            renderCondition: (e) => {
              const contentType = files?.find((f: FileBase) => f.id === e)?.contentType;
              if (contentType) return supportedFileType(contentType);
              return false;
            },
          },
          {
            title: 'Download',
            color: 'blue',
            showLoading: true,
            onSubmit: (e) => {
              startLoading();
              onDownloadItem(
                `${apiUrl.FILE_DOWNLOAD}?id=${e}`,
                files?.find((f: FileBase) => f.id === e)?.name,
              ).then(() => stopLoading());
            },
          },
        ]}
      />
      {selectedId && (
        <Modal
          open={previewOpen}
          title={files?.find((f: FileBase) => f.id === selectedId)?.name ?? 'Preview'}
          className={`min-w-[100%] min-h-screen !m-0 !rounded-none`}
          onClose={() => setPreviewOpen(false)}
        >
          <FilePreview id={selectedId} />
        </Modal>
      )}
    </>
  );
};
